import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, PageHeader, StatusBadge, TextInput, Toggle } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type LogRow, type LogsSummary } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { JsonDetails } from './Integrations/common';
import { PillSelect } from './reports/PillSelect';
import { CountUp } from '@/components/ui/CountUp';

/**
 * מסך הלוגים המאוחד: מה קרה במערכת — Webhooks, API, אירועים ופעילות —
 * בנפרד וביחד, על כל רוחב העמוד, עם סטטוס ברור (הצליח/נכשל/תקוע) ופעולת
 * "שלח שוב" למשלוח webhook כושל. עד עכשיו זה היה קבור בהגדרות tab=api.
 * 28/09/2026 (GoCall v2): מקורות כבורר מקטעים, חיפוש/סטטוס/רענון בצד השני,
 * וטבלה שקטה בכרטיס אחד ("מתי / מקור / מה קרה").
 */

type SourceChip = 'all' | 'webhooks' | 'api' | 'events' | 'activity';

const CHIPS: { id: SourceChip; he: string }[] = [
  { id: 'all', he: 'הכל' },
  { id: 'webhooks', he: 'Webhooks' },
  { id: 'api', he: 'API' },
  { id: 'events', he: 'אירועים' },
  { id: 'activity', he: 'פעילות' },
];

const SOURCE_HE: Record<LogRow['source'], string> = {
  webhook: 'Webhook',
  api: 'API',
  event: 'אירוע',
  activity: 'פעילות',
};
const SOURCE_TONE: Record<LogRow['source'], 'info' | 'ok' | 'warn' | 'neutral'> = {
  webhook: 'info',
  api: 'ok',
  event: 'neutral',
  activity: 'warn',
};
const STATUS_HE: Record<string, { he: string; tone: 'ok' | 'warn' | 'danger' | 'neutral' }> = {
  ok: { he: 'הצליח', tone: 'ok' },
  failed: { he: 'נכשל', tone: 'danger' },
  stuck: { he: 'תקוע', tone: 'warn' },
  pending: { he: 'ממתין', tone: 'neutral' },
};

export function AdminLogs() {
  const [params, setParams] = useSearchParams();
  const initial = (params.get('source') as SourceChip) || 'all';
  const [chip, setChip] = useState<SourceChip>(CHIPS.some((c) => c.id === initial) ? initial : 'all');
  const [status, setStatus] = useState<'' | 'ok' | 'failed' | 'stuck'>('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<LogRow[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [summary, setSummary] = useState<LogsSummary | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const qRef = useRef(q);
  qRef.current = q;

  const load = useCallback(async (append = false, before?: string) => {
    setLoading(true);
    try {
      const query = {
        q: qRef.current || undefined,
        status: status || undefined,
        before: before || undefined,
        limit: 50,
      };
      const res = chip === 'all'
        ? await adminApi.logsTimeline(query)
        : await adminApi.logsSource(chip, query);
      setRows((cur) => (append ? [...cur, ...res.data] : res.data));
      setNextBefore(res.next_before);
    } catch {
      if (!append) setRows([]);
    } finally {
      setLoading(false);
    }
  }, [chip, status]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { void adminApi.logsSummary().then(setSummary).catch(() => {}); }, []);

  // רענון אוטומטי — רק של העמוד הראשון (לא דורס טעינת "עוד").
  useEffect(() => {
    if (!autoRefresh) return;
    const t = window.setInterval(() => { void load(); void adminApi.logsSummary().then(setSummary).catch(() => {}); }, 10000);
    return () => window.clearInterval(t);
  }, [autoRefresh, load]);

  function pickChip(c: SourceChip) {
    setChip(c);
    setOpenKey(null);
    const next = new URLSearchParams(params);
    if (c === 'all') next.delete('source'); else next.set('source', c);
    setParams(next, { replace: true });
  }

  async function retry(row: LogRow) {
    setRetryingId(row.id);
    try {
      await adminApi.redeliverHubDelivery(row.id);
      toast('נשלח שוב — בודק תוצאה…', 'ok');
      void load();
    } catch {
      toast('שגיאה בשליחה חוזרת', 'danger');
    } finally {
      setRetryingId(null);
    }
  }

  const fmtTs = (ts: string | null) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const time = d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const today = d.toDateString() === new Date().toDateString();
    return `${today ? 'היום' : d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' })} ${time}`;
  };

  const th = { padding: '12px 10px', fontWeight: 500, whiteSpace: 'nowrap' as const };
  const td = { padding: '12px 10px' };

  return (
    <div className="stack page-enter" style={{ padding: '14px 16px 40px', maxWidth: 1280, width: '100%' }}>
      <PageHeader title="יומן פעילות" />

      {/* סרגל: מקורות בצד אחד; חיפוש, סטטוס ורענון בצד השני */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
          <div role="tablist" aria-label="מקור" className="seg">
            {CHIPS.map((c) => (
              <button key={c.id} type="button" role="tab" aria-selected={chip === c.id} onClick={() => pickChip(c.id)}>
                {c.he}
              </button>
            ))}
          </div>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ flex: '0 1 280px', minWidth: 180 }}>
          <TextInput
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
            placeholder="חיפוש (אירוע / נתיב / פעולה) — Enter"
            aria-label="חיפוש בלוגים"
          />
        </div>
        <PillSelect
          label="סטטוס" ariaLabel="סינון לפי סטטוס" value={status}
          onChange={(v) => setStatus(v)}
          options={[{ value: '', label: 'הכל' }, { value: 'ok', label: 'הצליח' }, { value: 'failed', label: 'נכשל' }, { value: 'stuck', label: 'תקוע' }]}
        />
        <span style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
          <Toggle checked={autoRefresh} onChange={setAutoRefresh} label="רענון אוטומטי" />
        </span>
      </div>

      {/* סיכום 24 שעות */}
      {summary && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>24 שעות אחרונות</span>
          <SummaryChip i={0} label="Webhooks" value={summary.webhooks_24h} bad={summary.webhooks_failed_24h} badLabel="נכשלו" />
          {summary.webhooks_stuck > 0 && (
            <SummaryChip i={1} label="תקועים" value={summary.webhooks_stuck} tone="warn" />
          )}
          <SummaryChip i={2} label="API" value={summary.api_24h} bad={summary.api_failed_24h} badLabel="שגיאות" />
          <SummaryChip i={3} label="אירועים" value={summary.events_24h} />
          <SummaryChip i={4} label="פעילות" value={summary.activity_24h} />
        </div>
      )}

      {/* הטבלה — כל רוחב העמוד */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)', color: 'var(--muted)', fontSize: 12 }}>
                <th style={{ ...th, textAlign: 'right', paddingInlineStart: 20 }}>מתי</th>
                <th style={{ ...th, textAlign: 'right' }}>מקור</th>
                <th style={{ ...th, textAlign: 'right' }}>מה קרה</th>
                <th style={{ ...th, textAlign: 'center' }}>סטטוס</th>
                <th style={{ ...th, textAlign: 'center' }}>HTTP</th>
                <th style={{ ...th, textAlign: 'center' }}>משך</th>
                <th style={{ ...th, paddingInlineEnd: 20 }} />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>אין רשומות לוג להצגה</td></tr>
              )}
              {rows.map((r, i) => {
                const key = `${r.source}-${r.id}`;
                const st = r.status ? STATUS_HE[r.status] : null;
                const open = openKey === key;
                return (
                  <Fragment key={key}>
                    {/* כניסה מדורגת (gcRow, 0.02+i*0.03 כמו בעיצוב). מפתח יציב ⇒ רענון
                        אוטומטי לא מריץ את הכניסה שוב, רק שורות חדשות נכנסות. */}
                    <tr
                      onClick={() => setOpenKey(open ? null : key)}
                      aria-expanded={open}
                      className="anim-row hover-row"
                      style={{ borderBottom: '1px solid var(--line)', cursor: 'pointer', background: open ? 'var(--surface-2)' : undefined, ['--d' as string]: `${0.02 + Math.min(i, 15) * 0.03}s` }}
                    >
                      <td style={{ ...td, paddingInlineStart: 20, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'var(--text-2)' }} title={r.ts ?? undefined}>{fmtTs(r.ts)}</td>
                      <td style={td}><StatusBadge tone={SOURCE_TONE[r.source]} size="sm">{SOURCE_HE[r.source]}</StatusBadge></td>
                      <td style={{ ...td, fontWeight: 500, maxWidth: 440, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.title}>{r.title}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {st ? <StatusBadge tone={st.tone} size="sm" dot>{st.he}</StatusBadge> : <span style={{ color: 'var(--faint)' }}>—</span>}
                      </td>
                      <td style={{ ...td, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: r.http_status && r.http_status >= 400 ? 'var(--danger)' : 'var(--text-2)' }}>{r.http_status ?? '—'}</td>
                      <td style={{ ...td, textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: 'var(--muted)' }}>
                        {r.latency_ms != null ? `${r.latency_ms}ms` : '—'}
                      </td>
                      <td style={{ ...td, paddingInlineEnd: 20, textAlign: 'left' }} onClick={(e) => e.stopPropagation()}>
                        {r.source === 'webhook' && r.retryable && (
                          <button
                            type="button"
                            className="btn soft"
                            disabled={retryingId === r.id}
                            onClick={() => void retry(r)}
                            style={{ height: 30, fontSize: 'var(--t-xs)', padding: '0 12px' }}
                          >
                            <Icon name="bolt" size={12} /> {retryingId === r.id ? 'שולח…' : 'שלח שוב'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="anim-fade" style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
                        <td colSpan={7} style={{ padding: '10px 20px 14px' }}>
                          <JsonDetails value={r.detail ?? {}} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {nextBefore && rows.length >= 50 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 16px', borderTop: '1px solid var(--line)' }}>
            <button type="button" className="btn ghost" disabled={loading} onClick={() => void load(true, nextBefore)}>
              {loading ? 'טוען…' : 'טען עוד'}
            </button>
          </div>
        )}
      </div>

      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
        כלי הבדיקה של ה-API (קונסולה + מעקב חי) נמצאים ב<Link to="/admin/settings?tab=api">הגדרות ← API</Link>;
        ניהול ה-Webhooks עצמם ב<Link to="/admin/settings?tab=webhooks">הגדרות ← Webhooks</Link>.
      </div>
    </div>
  );
}

/** גלולת סיכום: נכנסת מדורגת, המספר נספר (וממשיך מהערך הקודם ברענון). */
function SummaryChip({ label, value, bad, badLabel, tone, i = 0 }: { label: string; value?: number; bad?: number; badLabel?: string; tone?: 'warn'; i?: number }) {
  return (
    <div className="anim-up" style={{
      ['--d' as string]: `${0.05 + i * 0.05}s`,
      display: 'inline-flex', alignItems: 'center', gap: 8,
      height: 28, padding: '0 12px', borderRadius: 'var(--r-pill)',
      background: tone === 'warn' ? 'var(--warn-soft)' : 'var(--surface-3)',
      fontSize: 'var(--t-xs)',
    }}>
      <span style={{ color: 'var(--text-2)' }}>{label}</span>
      <strong style={{ fontVariantNumeric: 'tabular-nums' }}><CountUp value={value ?? 0} /></strong>
      {bad != null && bad > 0 && <span style={{ color: 'var(--danger)' }}>· {bad} {badLabel}</span>}
    </div>
  );
}
